import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AxiosError } from 'axios'
import joinTextValues from 'utils/joinTextValues'

export interface CopyingBlocker {
    type: string
    message: string
}

export interface ApplicationStatusProps {
    id: number
    first_name: string
    last_name: string
    status: string
    lender_name: string
    submitted_at: string
    created: string
    asset_type?: string
    copying_blocker?: CopyingBlocker | null
    has_active_contracts?: boolean
    redecision_blockers?: Array<{
        type: string
        message: string
    }>
}

type UseReadOnlyApplicationResult = {
    id: string | undefined
    error: Error | null
    loading: boolean
    appData: object
    appStatus: ApplicationStatusProps
    applicantName: string
}

type DataFetcher = (
    id: string,
    abortController: AbortController,
) => Promise<{ appValues: object; app: ApplicationStatusProps }>

export function useReadOnlyApplication(
    dataFetcher: DataFetcher,
): UseReadOnlyApplicationResult {
    const { id } = useParams()
    const [error, setError] = useState<Error | null>(null)
    const [loading, setLoading] = useState(true)
    const [appData, setAppData] = useState({})
    const [appStatus, setAppStatus] = useState({} as ApplicationStatusProps)

    useEffect(() => {
        const abortController = new AbortController()

        if (!id) {
            return () => abortController.abort()
        }

        dataFetcher(id, abortController)
            .then((applicationData) => {
                setAppData(applicationData.appValues)
                setAppStatus(applicationData.app)
                setError(null)
                setLoading(false)
            })
            .catch((appError) => {
                if (appError.code === AxiosError.ERR_CANCELED) {
                    return
                }

                setError(appError)
                setLoading(false)
            })

        return () => {
            abortController.abort()
        }
    }, [id, dataFetcher])

    const applicantName = joinTextValues([
        appStatus.first_name,
        appStatus.last_name,
    ])

    return {
        id,
        error,
        loading,
        appData,
        appStatus,
        applicantName,
    }
}
